import axios from "axios";

/**
 * The research chat — the factor-alpha bot, reached over HTTP.
 *
 * Same bot as Telegram, second door. The bot verifies our StockReco JWT
 * against the backend itself, so we send the token we already hold and
 * nothing else. See factor-alpha/WEB_CHAT.md for the contract.
 *
 *   REACT_APP_CHAT_API_URL   e.g. https://factor-alpha.up.railway.app
 */
export const chat_base_url = (process.env.REACT_APP_CHAT_API_URL || "").replace(/\/+$/, "");

export const chatConfigured = () => Boolean(chat_base_url);

// Scoring a call or a batch report can take a minute or two; a plain lookup
// is sub-second. One generous budget rather than a per-command table.
const CHAT_TIMEOUT_MS = 180_000;

const authHeaders = () => {
    const jwtToken = sessionStorage.getItem("authToken");
    return jwtToken ? { Authorization: `Bearer ${jwtToken}` } : null;
};

/**
 * Send one message. Resolves to
 *   { success, replies: [{type:"text", text, markdown} | {type:"file", name, caption, size, mime, url}], sent, busy, choices? }
 *   choices (e.g. ["AAPL","GOOGL","both"]) means the bot is asking which stock the question is about —
 *   reply with one of them (or any message) to continue.
 * or { success:false, authExpired } / { success:false, error, offline }.
 */
export const sendChatMessage = async (message, { ticker } = {}) => {
    if (!chatConfigured()) {
        return { success: false, error: "The research chat is not configured (REACT_APP_CHAT_API_URL).", offline: true };
    }
    const headers = authHeaders();
    if (!headers) return { success: false, authExpired: true };

    try {
        const response = await axios.post(
            `${chat_base_url}/chat`,
            { message, ticker: ticker || undefined },
            { headers: { ...headers, "Content-Type": "application/json" }, timeout: CHAT_TIMEOUT_MS, withCredentials: false }
        );
        const data = response.data || {};
        const replies = (data.replies || []).map((r) =>
            r.type === "file" ? { ...r, url: `${chat_base_url}${r.url}` } : r
        );
        // `sent` is what ran (a string, or an array when the bot ran the question for
        // more than one stock); `choices` is set when the bot is asking which stock
        // a bare follow-up is about (page stock vs the one named earlier, or both).
        const sent = Array.isArray(data.sent) ? data.sent.join("  ·  ") : data.sent;
        return { success: true, replies, sent, choices: data.choices || null, busy: Boolean(data.busy), elapsedMs: data.elapsed_ms };
    } catch (error) {
        if (error?.response?.status === 401) return { success: false, authExpired: true };
        if (error?.code === "ECONNABORTED") {
            return { success: false, error: "The bot is still working — that took longer than three minutes. Try again in a moment." };
        }
        if (!error?.response) {
            return { success: false, offline: true, error: "The research bot is unreachable right now." };
        }
        const detail = error?.response?.data?.detail || error?.response?.data?.error;
        return { success: false, error: detail ? `The bot refused: ${detail}` : "The bot returned an error." };
    }
};

/** Cheap connectivity + identity check for the widget header. */
export const pingChat = async () => {
    if (!chatConfigured()) return { success: false, offline: true };
    const headers = authHeaders();
    if (!headers) return { success: false, authExpired: true };
    try {
        const response = await axios.get(`${chat_base_url}/chat/ping`, { headers, timeout: 15_000 });
        return { success: true, data: response.data };
    } catch (error) {
        if (error?.response?.status === 401) return { success: false, authExpired: true };
        return { success: false, offline: true };
    }
};

/**
 * The bot's own record of this user's conversation — what it remembers,
 * from any device. Resolves to { success, messages: [{role, text, at}] }.
 */
export const getChatHistory = async (limit = 40) => {
    if (!chatConfigured()) return { success: false, offline: true };
    const headers = authHeaders();
    if (!headers) return { success: false, authExpired: true };
    try {
        const response = await axios.get(`${chat_base_url}/chat/history`, { headers, params: { limit }, timeout: 20_000 });
        return { success: true, messages: response.data?.messages || [] };
    } catch (error) {
        if (error?.response?.status === 401) return { success: false, authExpired: true };
        return { success: false, offline: true };
    }
};

/**
 * Download a report the bot produced. The link is owner-bound and needs the
 * bearer token, so a plain <a href> would 401 — fetch it and hand the bytes
 * to the browser as a blob.
 */
export const downloadChatFile = async (file) => {
    const headers = authHeaders();
    if (!headers) return { success: false, authExpired: true };
    try {
        const response = await axios.get(file.url, { headers, responseType: "blob", timeout: 60_000 });
        const blobUrl = URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = file.name || "report";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
        return { success: true };
    } catch (error) {
        if (error?.response?.status === 401) return { success: false, authExpired: true };
        if (error?.response?.status === 404) return { success: false, error: "That report link has expired — ask the bot again." };
        return { success: false, error: "Could not download the file." };
    }
};
